// ═══════════════════════════════════════════════════════════════════════════
// Block authoring DXF parsers (Phase K.5)
// ═══════════════════════════════════════════════════════════════════════════
//
// Per spec §4.5 templates, decode each AutoCAD authoring class from a
// DxfDynamicBlockObject (handle + ownerHandle + raw group list) into
// the typed BlockAuthoringParameter / BlockAuthoringAction shape.
//
// These parsers are subclass-aware: they partition `obj.rawGroups` by
// AcDb subclass marker (group 100) and extract fields from the
// appropriate slice.
//
// Class name normalisation:
//   - DXF emits both `BLOCKLINEARPARAMETER` and `ACDBBLOCKLINEARPARAMETER`
//     spellings (the prefixed form in older fixtures, the un-prefixed
//     form in AutoCAD 2018+). We strip the leading `ACDB` if present
//     to dispatch on a single canonical key.

import {
  decodeDxfUnicode,
  type DxfDynamicBlockObject,
  type DxfGroup,
} from "@dxf-ai-editor/dxf-io";

import {
  type AlignmentAuthoringParameter,
  type ArrayAuthoringAction,
  type BasepointAuthoringParameter,
  type BlockAuthoringAction,
  type BlockAuthoringParameter,
  type EvalGraph,
  type EvalGraphNode,
  type FlipAuthoringAction,
  type FlipAuthoringParameter,
  type LinearAuthoringParameter,
  type LookupAuthoringAction,
  type LookupAuthoringParameter,
  type LookupColumn,
  type MoveAuthoringAction,
  type PointAuthoringParameter,
  type PolarAuthoringParameter,
  type PolarStretchAuthoringAction,
  type RotationAuthoringAction,
  type RotationAuthoringParameter,
  type ScaleAuthoringAction,
  type StretchAuthoringAction,
  type StretchAxisFlag,
  type AuthoringVisibilityState,
  type VisibilityAuthoringParameter,
  type XYAuthoringParameter,
  evalGraphSchema,
} from "../blockAuthoring";
import {
  allGroups,
  firstGroup,
  firstInt,
  firstNumber,
  parseLinearValueSet,
  parsePoint,
  partitionSubclasses,
} from "./helpers";

/**
 * Strip the leading `ACDB` prefix if present so the dispatch table
 * sees a single canonical class name.
 */
export function canonicalClassName(type: string): string {
  return type.startsWith("ACDB") ? type.slice(4) : type;
}

// ═══════════════════════════════════════════════════════════════════════════
// PARAMETER PARSERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Common-fields extractor — every BLOCK<KIND>PARAMETER has these
 * subclass markers populated.
 */
function readParameterCommon(
  obj: DxfDynamicBlockObject,
  classes: Map<string, DxfGroup[]>,
): {
  evalExprNodeId: number;
  name: string;
  paletteShow: boolean;
  baseLocation: number;
  valueSetFlag: number;
} {
  const evalExpr = classes.get("AcDbEvalExpr") ?? [];
  const blockElement = classes.get("AcDbBlockElement") ?? [];
  const blockParameter = classes.get("AcDbBlockParameter") ?? [];

  return {
    // 90 in AcDbEvalExpr = node id
    evalExprNodeId: firstInt(evalExpr, 90) ?? 0,
    // 300 in AcDbBlockElement = display name
    name: firstGroup(blockElement, 300) ?? "",
    // 1071 in AcDbBlockElement = palette show flag
    paletteShow: (firstInt(blockElement, 1071) ?? 1) !== 0,
    // 280 in AcDbBlockParameter = base location anchor
    baseLocation: firstInt(blockParameter, 280) ?? 0,
    // 281 in AcDbBlockParameter = value-set flag
    valueSetFlag: firstInt(blockParameter, 281) ?? 0,
  };
}

// ─── 1. Point Parameter ──────────────────────────────────────────────

export function acDbBlockPointParameterParser(
  obj: DxfDynamicBlockObject,
): PointAuthoringParameter {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readParameterCommon(obj, classes);
  const oneP = classes.get("AcDbBlock1PtParameter") ?? [];
  const point = classes.get("AcDbBlockPointParameter") ?? [];

  return {
    kind: "point",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    paletteShow: common.paletteShow,
    baseLocation: common.baseLocation,
    valueSetFlag: common.valueSetFlag,
    basePoint: parsePoint(oneP, 1010, 1020, 1030),
    label: firstGroup(point, 305) ?? "",
    description: firstGroup(point, 306) ?? "",
    xOutputNodeId: firstInt(point, 96) ?? 0,
    yOutputNodeId: firstInt(point, 97) ?? 0,
  };
}

// ─── 2. Linear Parameter ─────────────────────────────────────────────

export function acDbBlockLinearParameterParser(
  obj: DxfDynamicBlockObject,
): LinearAuthoringParameter {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readParameterCommon(obj, classes);
  const twoP = classes.get("AcDbBlock2PtParameter") ?? [];
  const linear = classes.get("AcDbBlockLinearParameter") ?? [];

  // displacementXProperty = 303, displacementYProperty = 304 in
  // AcDbBlock2PtParameter.
  return {
    kind: "linear",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    paletteShow: common.paletteShow,
    baseLocation: common.baseLocation,
    valueSetFlag: common.valueSetFlag,
    startPoint: parsePoint(twoP, 1010, 1020, 1030),
    endPoint: parsePoint(twoP, 1011, 1021, 1031),
    displacementXProperty: firstGroup(twoP, 303) ?? "DisplacementX",
    displacementYProperty: firstGroup(twoP, 304) ?? "DisplacementY",
    label: firstGroup(linear, 305) ?? "",
    description: firstGroup(linear, 306) ?? "",
    distance: firstNumber(linear, 140) ?? 0,
    unitFormat: firstGroup(linear, 307) ?? "",
    inputNodeId: firstInt(linear, 96) ?? 0,
    valueSet: parseLinearValueSet(linear),
  };
}

// ─── 3. Polar Parameter ──────────────────────────────────────────────

export function acDbBlockPolarParameterParser(
  obj: DxfDynamicBlockObject,
): PolarAuthoringParameter {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readParameterCommon(obj, classes);
  const twoP = classes.get("AcDbBlock2PtParameter") ?? [];
  const polar = classes.get("AcDbBlockPolarParameter") ?? [];

  return {
    kind: "polar",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    paletteShow: common.paletteShow,
    baseLocation: common.baseLocation,
    valueSetFlag: common.valueSetFlag,
    startPoint: parsePoint(twoP, 1010, 1020, 1030),
    endPoint: parsePoint(twoP, 1011, 1021, 1031),
    label: firstGroup(polar, 305) ?? "",
    description: firstGroup(polar, 306) ?? "",
    distance: firstNumber(polar, 140) ?? 0,
    angle: firstNumber(polar, 141) ?? 0,
    unitFormat: firstGroup(polar, 307) ?? "",
    inputNodeId: firstInt(polar, 96) ?? 0,
    distanceValueSet: parseLinearValueSet(polar),
    // Angle value-set codes are at a different offset; for now mirror
    // the distance shape with empty defaults — fixture coverage adds
    // exact codes when polar fixture surfaces.
    angleValueSet: { mode: "none", min: 0, max: 0, increment: 0, listValues: [] },
  };
}

// ─── 4. XY Parameter ─────────────────────────────────────────────────

export function acDbBlockXyParameterParser(
  obj: DxfDynamicBlockObject,
): XYAuthoringParameter {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readParameterCommon(obj, classes);
  const twoP = classes.get("AcDbBlock2PtParameter") ?? [];
  const xy = classes.get("AcDbBlockXYParameter") ?? [];

  return {
    kind: "xy",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    paletteShow: common.paletteShow,
    baseLocation: common.baseLocation,
    valueSetFlag: common.valueSetFlag,
    startPoint: parsePoint(twoP, 1010, 1020, 1030),
    endPoint: parsePoint(twoP, 1011, 1021, 1031),
    label: firstGroup(xy, 305) ?? "",
    description: firstGroup(xy, 306) ?? "",
    distanceX: firstNumber(xy, 140) ?? 0,
    distanceY: firstNumber(xy, 141) ?? 0,
    unitFormat: firstGroup(xy, 307) ?? "",
    inputNodeId: firstInt(xy, 96) ?? 0,
    xValueSet: parseLinearValueSet(xy),
    yValueSet: { mode: "none", min: 0, max: 0, increment: 0, listValues: [] },
  };
}

// ─── 5. Rotation Parameter ───────────────────────────────────────────

export function acDbBlockRotationParameterParser(
  obj: DxfDynamicBlockObject,
): RotationAuthoringParameter {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readParameterCommon(obj, classes);
  const twoP = classes.get("AcDbBlock2PtParameter") ?? [];
  const rot = classes.get("AcDbBlockRotationParameter") ?? [];

  return {
    kind: "rotation",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    paletteShow: common.paletteShow,
    baseLocation: common.baseLocation,
    valueSetFlag: common.valueSetFlag,
    basePoint: parsePoint(twoP, 1010, 1020, 1030),
    refPoint: parsePoint(twoP, 1011, 1021, 1031),
    label: firstGroup(rot, 305) ?? "",
    description: firstGroup(rot, 306) ?? "",
    angle: firstNumber(rot, 140) ?? 0,
    unitFormat: firstGroup(rot, 307) ?? "",
    inputNodeId: firstInt(rot, 96) ?? 0,
    angleValueSet: parseLinearValueSet(rot),
  };
}

// ─── 6. Alignment Parameter ──────────────────────────────────────────

export function acDbBlockAlignmentParameterParser(
  obj: DxfDynamicBlockObject,
): AlignmentAuthoringParameter {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readParameterCommon(obj, classes);
  const twoP = classes.get("AcDbBlock2PtParameter") ?? [];
  const align = classes.get("AcDbBlockAlignmentParameter") ?? [];

  // 280 in AcDbBlockAlignmentParameter: 0 = perpendicular, 1 = tangent.
  const alignmentType =
    (firstInt(align, 280) ?? 0) === 1 ? "tangent" : "perpendicular";

  return {
    kind: "alignment",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    paletteShow: common.paletteShow,
    baseLocation: common.baseLocation,
    valueSetFlag: common.valueSetFlag,
    basePoint: parsePoint(twoP, 1010, 1020, 1030),
    alignmentDirection: parsePoint(twoP, 1011, 1021, 1031),
    alignmentType,
  };
}

// ─── 7. Flip Parameter ───────────────────────────────────────────────

export function acDbBlockFlipParameterParser(
  obj: DxfDynamicBlockObject,
): FlipAuthoringParameter {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readParameterCommon(obj, classes);
  const twoP = classes.get("AcDbBlock2PtParameter") ?? [];
  const flip = classes.get("AcDbBlockFlipParameter") ?? [];

  // Grip position in flip = 1012/1022/1032 (offset from line).
  const gripX = firstNumber(flip, 1012);
  const gripY = firstNumber(flip, 1022);
  const gripZ = firstNumber(flip, 1032);
  const gripPosition =
    gripX !== undefined && gripY !== undefined
      ? { x: gripX, y: gripY, z: gripZ ?? 0 }
      : undefined;

  return {
    kind: "flip",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    paletteShow: common.paletteShow,
    baseLocation: common.baseLocation,
    valueSetFlag: common.valueSetFlag,
    reflectionLineStart: parsePoint(twoP, 1010, 1020, 1030),
    reflectionLineEnd: parsePoint(twoP, 1011, 1021, 1031),
    label: firstGroup(flip, 305) ?? "",
    description: firstGroup(flip, 306) ?? "",
    baseStateLabel: firstGroup(flip, 307) ?? "Base",
    flippedStateLabel: firstGroup(flip, 308) ?? "Flipped",
    flipPropertyName: firstGroup(flip, 309) ?? "UpdatedFlip",
    outputNodeId: firstInt(flip, 96) ?? 0,
    flipState: 0,
    ...(gripPosition ? { gripPosition } : {}),
  };
}

// ─── 8. Visibility Parameter ─────────────────────────────────────────

export function acDbBlockVisibilityParameterParser(
  obj: DxfDynamicBlockObject,
): VisibilityAuthoringParameter {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readParameterCommon(obj, classes);
  const oneP = classes.get("AcDbBlock1PtParameter") ?? [];
  const vis = classes.get("AcDbBlockVisibilityParameter") ?? [];

  // The visibility parameter has a SEQUENTIAL group layout described
  // in spec §3.1: 91 0 / 93 N / 331×N / 95 S / for-each-state {333,
  // 333, 303, 94 K, 332×K}. We must walk the slice sequentially
  // because the same code (333, 303, 94, 332) repeats per state.
  const masterEntityHandles: string[] = [];
  const states: AuthoringVisibilityState[] = [];

  // Sequential walk per fixture observation:
  //   91 0           (active state index)
  //   93 N           (master entity count)
  //   331 × N        (master entity handles)
  //   92 S           (state count, NOT 95 as in incomplete spec)
  //   for each state: 303 name / 94 K / 332×K / 95 G / 333×G
  let i = 0;
  // Find first 93 (master entity count).
  while (i < vis.length) {
    const g = vis[i];
    if (g && g.code === 93) break;
    i += 1;
  }
  if (i < vis.length) {
    const g = vis[i];
    const n = g ? Number.parseInt(g.value, 10) : 0;
    i += 1;
    let consumed = 0;
    while (i < vis.length && consumed < n) {
      const g2 = vis[i];
      if (g2 && g2.code === 331) {
        masterEntityHandles.push(g2.value);
        consumed += 1;
      }
      i += 1;
    }
  }

  // State count — spec calls it 95 but real fixtures use 92.
  let stateCount = 0;
  while (i < vis.length) {
    const g = vis[i];
    if (g && (g.code === 92 || g.code === 95)) {
      stateCount = Number.parseInt(g.value, 10);
      i += 1;
      break;
    }
    i += 1;
  }

  // Per state, parse: 303 name / 94 K visible-count / 332×K /
  //                   95 G state-grip-count / 333×G grip handles.
  for (let s = 0; s < stateCount && i < vis.length; s += 1) {
    let stateName = "";
    const visibleEntityHandles: string[] = [];
    const stateGripHandles: string[] = [];

    // Find 303.
    while (i < vis.length) {
      const g = vis[i];
      if (g && g.code === 303) {
        stateName = decodeDxfUnicode(g.value); // T9 PR-3 — visibility-state name decode
        i += 1;
        break;
      }
      i += 1;
    }
    // Find 94 K (visible count).
    while (i < vis.length) {
      const g = vis[i];
      if (g && g.code === 94) {
        const k = Number.parseInt(g.value, 10);
        i += 1;
        let consumed = 0;
        while (i < vis.length && consumed < k) {
          const g2 = vis[i];
          if (g2 && g2.code === 332) {
            visibleEntityHandles.push(g2.value);
            consumed += 1;
          }
          i += 1;
        }
        break;
      }
      i += 1;
    }
    // Find 95 G (state grip count, optional).
    if (i < vis.length) {
      const peek = vis[i];
      if (peek && peek.code === 95) {
        const ng = Number.parseInt(peek.value, 10);
        i += 1;
        let consumed = 0;
        while (i < vis.length && consumed < ng) {
          const g2 = vis[i];
          if (g2 && g2.code === 333) {
            stateGripHandles.push(g2.value);
            consumed += 1;
          }
          i += 1;
        }
      }
    }

    states.push({
      name: stateName,
      visibleEntityHandles,
      stateGripHandles,
    });
  }

  return {
    kind: "visibility",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    paletteShow: common.paletteShow,
    baseLocation: common.baseLocation,
    valueSetFlag: common.valueSetFlag,
    gripPosition: parsePoint(oneP, 1010, 1020, 1030),
    currentStateName: firstGroup(vis, 301) ?? "",
    description: firstGroup(vis, 302) ?? "",
    activeStateIndex: firstInt(vis, 91) ?? 0,
    masterEntityHandles,
    states,
  };
}

// ─── 9. Lookup Parameter ─────────────────────────────────────────────

export function acDbBlockLookUpParameterParser(
  obj: DxfDynamicBlockObject,
): LookupAuthoringParameter {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readParameterCommon(obj, classes);
  const oneP = classes.get("AcDbBlock1PtParameter") ?? [];
  const lookup = classes.get("AcDbBlockLookUpParameter") ?? [];

  return {
    kind: "lookup",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    paletteShow: common.paletteShow,
    baseLocation: common.baseLocation,
    valueSetFlag: common.valueSetFlag,
    gripPosition: parsePoint(oneP, 1010, 1020, 1030),
    label: firstGroup(lookup, 303) ?? "",
    description: firstGroup(lookup, 304) ?? "",
    inputNodeId: firstInt(lookup, 94) ?? 0,
    currentLookupValue: "",
  };
}

// ─── 10. Basepoint Parameter ─────────────────────────────────────────

export function acDbBlockBasepointParameterParser(
  obj: DxfDynamicBlockObject,
): BasepointAuthoringParameter {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readParameterCommon(obj, classes);
  const oneP = classes.get("AcDbBlock1PtParameter") ?? [];
  const bp = classes.get("AcDbBlockBasepointParameter") ?? [];

  return {
    kind: "basepoint",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    paletteShow: common.paletteShow,
    baseLocation: common.baseLocation,
    valueSetFlag: common.valueSetFlag,
    basePoint: parsePoint(oneP, 1010, 1020, 1030),
    referencePoint: parsePoint(bp, 1011, 1021, 1031),
    updatedBasePoint: parsePoint(bp, 1012, 1022, 1032),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// ACTION PARSERS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Common-fields extractor for AcDbBlockAction subclass (selection
 * count + handle list + display anchor).
 */
function readActionCommon(
  obj: DxfDynamicBlockObject,
  classes: Map<string, DxfGroup[]>,
): {
  evalExprNodeId: number;
  name: string;
  flags: number;
  entityHandles: string[];
  displayAnchor: { x: number; y: number; z: number };
} {
  const evalExpr = classes.get("AcDbEvalExpr") ?? [];
  const blockElement = classes.get("AcDbBlockElement") ?? [];
  const blockAction = classes.get("AcDbBlockAction") ?? [];

  return {
    evalExprNodeId: firstInt(evalExpr, 90) ?? 0,
    name: firstGroup(blockElement, 300) ?? "",
    flags: firstInt(blockAction, 70) ?? 0,
    // 330 in AcDbBlockAction = entity selection handles (variable count)
    entityHandles: allGroups(blockAction, 330),
    displayAnchor: parsePoint(blockAction, 1010, 1020, 1030),
  };
}

// ─── 1. Move Action ──────────────────────────────────────────────────

export function acDbBlockMoveActionParser(
  obj: DxfDynamicBlockObject,
): MoveAuthoringAction {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readActionCommon(obj, classes);
  const move = classes.get("AcDbBlockMoveAction") ?? [];

  return {
    kind: "move",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    flags: common.flags,
    entityHandles: common.entityHandles,
    displayAnchor: common.displayAnchor,
    parameterId: "",
    xInputNodeId: firstInt(move, 92) ?? 0,
    yInputNodeId: firstInt(move, 93) ?? 0,
    xDeltaProperty: firstGroup(move, 301) ?? "EndXDelta",
    yDeltaProperty: firstGroup(move, 302) ?? "EndYDelta",
    scaleFactor: firstNumber(move, 140) ?? 1,
    angleOffset: firstNumber(move, 141) ?? 0,
  };
}

// ─── 2. Scale Action ─────────────────────────────────────────────────

export function acDbBlockScaleActionParser(
  obj: DxfDynamicBlockObject,
): ScaleAuthoringAction {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readActionCommon(obj, classes);
  const wbp = classes.get("AcDbBlockActionWithBasePt") ?? [];
  const scale = classes.get("AcDbBlockScaleAction") ?? [];

  // 280 in AcDbBlockScaleAction: 0 = independent, 1 = uniform.
  const scaleTypeFlag = firstInt(scale, 280) ?? 0;
  const scaleType: ScaleAuthoringAction["scaleType"] =
    scaleTypeFlag === 1 ? "uniform" : "independent";

  return {
    kind: "scale",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    flags: common.flags,
    entityHandles: common.entityHandles,
    displayAnchor: common.displayAnchor,
    parameterId: "",
    basePoint: parsePoint(wbp, 1011, 1021, 1031),
    updatedBasePoint: parsePoint(wbp, 1012, 1022, 1032),
    baseInputCountA: firstInt(wbp, 92) ?? 1,
    baseInputCountB: firstInt(wbp, 93) ?? 1,
    baseUpdateXProperty: firstGroup(wbp, 301) ?? "UpdatedBaseX",
    baseUpdateYProperty: firstGroup(wbp, 302) ?? "UpdatedBaseY",
    scaleXInputId: firstInt(scale, 94) ?? 0,
    scaleYInputId: firstInt(scale, 95) ?? 0,
    combinedScaleInputId: firstInt(scale, 96) ?? 0,
    scaleProperty: firstGroup(scale, 303) ?? "Scale",
    xScaleProperty: firstGroup(scale, 304) ?? "XScale",
    yScaleProperty: firstGroup(scale, 305) ?? "YScale",
    scaleType,
  };
}

// ─── 3. Stretch Action ───────────────────────────────────────────────

export function acDbBlockStretchActionParser(
  obj: DxfDynamicBlockObject,
): StretchAuthoringAction {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readActionCommon(obj, classes);
  const stretch = classes.get("AcDbBlockStretchAction") ?? [];

  // SEQUENTIAL parse: stretch's slice contains ordered sections per
  // spec §4.5.9:
  //   92 X_input
  //   301 EndXDelta
  //   93 Y_input
  //   302 EndYDelta
  //   72 fence_count + N pairs (1011, 1021)
  //   73 secondary_count + N × 331
  //   74 axis_count + per-entity (94)
  //   75 0
  //   140 scale, 141 angle, 280 flag
  let xInputNodeId = 0;
  let xDeltaProperty = "EndXDelta";
  let yInputNodeId = 0;
  let yDeltaProperty = "EndYDelta";
  const crossingFence: { x: number; y: number; z: number }[] = [];
  const secondaryEntityHandles: string[] = [];
  const axisFlags: StretchAxisFlag[] = [];
  let reservedFlag = 0;
  let scaleFactor = 1;
  let angleOffset = 0;

  let i = 0;
  while (i < stretch.length) {
    const g = stretch[i];
    if (!g) {
      i += 1;
      continue;
    }
    if (g.code === 92 && xInputNodeId === 0) {
      xInputNodeId = Number.parseInt(g.value, 10);
    } else if (g.code === 301 && xDeltaProperty === "EndXDelta") {
      xDeltaProperty = decodeDxfUnicode(g.value); // T9 PR-3 — delta property name decode
    } else if (g.code === 93 && yInputNodeId === 0) {
      yInputNodeId = Number.parseInt(g.value, 10);
    } else if (g.code === 302 && yDeltaProperty === "EndYDelta") {
      yDeltaProperty = decodeDxfUnicode(g.value); // T9 PR-3 — delta property name decode
    } else if (g.code === 72) {
      const fenceN = Number.parseInt(g.value, 10);
      i += 1;
      let consumed = 0;
      while (i < stretch.length && consumed < fenceN) {
        const cur = stretch[i];
        const next = stretch[i + 1];
        // Each fence vertex is a (1011, 1021) pair.
        if (cur && cur.code === 1011) {
          const px = Number.parseFloat(cur.value);
          let py = 0;
          if (next && next.code === 1021) {
            py = Number.parseFloat(next.value);
            crossingFence.push({ x: px, y: py, z: 0 });
            i += 2;
          } else {
            crossingFence.push({ x: px, y: 0, z: 0 });
            i += 1;
          }
          consumed += 1;
        } else {
          i += 1;
        }
      }
      continue;
    } else if (g.code === 73) {
      const secondaryN = Number.parseInt(g.value, 10);
      i += 1;
      let consumed = 0;
      while (i < stretch.length && consumed < secondaryN) {
        const cur = stretch[i];
        if (cur && cur.code === 331) {
          secondaryEntityHandles.push(cur.value);
          consumed += 1;
        }
        i += 1;
      }
      continue;
    } else if (g.code === 74) {
      const axisN = Number.parseInt(g.value, 10);
      i += 1;
      let consumed = 0;
      while (i < stretch.length && consumed < axisN) {
        const cur = stretch[i];
        if (cur && cur.code === 94) {
          axisFlags.push({
            flag: 1,
            axisId: Number.parseInt(cur.value, 10),
          });
          consumed += 1;
        }
        i += 1;
      }
      continue;
    } else if (g.code === 75) {
      reservedFlag = Number.parseInt(g.value, 10);
    } else if (g.code === 140) {
      scaleFactor = Number.parseFloat(g.value);
    } else if (g.code === 141) {
      angleOffset = Number.parseFloat(g.value);
    }
    i += 1;
  }

  return {
    kind: "stretch",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    flags: common.flags,
    entityHandles: common.entityHandles,
    displayAnchor: common.displayAnchor,
    parameterId: "",
    xInputNodeId,
    yInputNodeId,
    xDeltaProperty,
    yDeltaProperty,
    crossingFence,
    secondaryEntityHandles,
    axisFlags,
    reservedFlag,
    scaleFactor,
    angleOffset,
  };
}

// ─── 4. Polar Stretch Action ─────────────────────────────────────────

export function acDbBlockPolarStretchActionParser(
  obj: DxfDynamicBlockObject,
): PolarStretchAuthoringAction {
  // Reuse the Stretch parser logic, then layer the rotation set on top.
  // PolarStretch fixture is not present in current dump; we parse the
  // same fields as Stretch and treat any extra 331 handles in
  // AcDbBlockPolarStretchAction subclass as the rotation set.
  const baseStretch = acDbBlockStretchActionParser(obj);
  const classes = partitionSubclasses(obj.rawGroups);
  const polar = classes.get("AcDbBlockPolarStretchAction") ?? [];

  // Per spec, PolarStretch additionally stores a rotation set via 75/331.
  // We collect any 331 handles that appear after the regular stretch
  // section as the rotation set.
  const rotationEntityHandles = allGroups(polar, 331);

  return {
    ...baseStretch,
    kind: "polar-stretch",
    rotationEntityHandles,
  };
}

// ─── 5. Rotation Action (BLOCKROTATEACTION) ──────────────────────────

export function acDbBlockRotationActionParser(
  obj: DxfDynamicBlockObject,
): RotationAuthoringAction {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readActionCommon(obj, classes);
  const wbp = classes.get("AcDbBlockActionWithBasePt") ?? [];
  // Action class label is BLOCKROTATEACTION but C++ subclass is
  // AcDbBlockRotationAction.
  const rot = classes.get("AcDbBlockRotationAction") ?? [];

  return {
    kind: "rotation",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    flags: common.flags,
    entityHandles: common.entityHandles,
    displayAnchor: common.displayAnchor,
    parameterId: "",
    basePoint: parsePoint(wbp, 1011, 1021, 1031),
    updatedBasePoint: parsePoint(wbp, 1012, 1022, 1032),
    baseInputCountA: firstInt(wbp, 92) ?? 1,
    baseInputCountB: firstInt(wbp, 93) ?? 1,
    baseUpdateXProperty: firstGroup(wbp, 301) ?? "UpdatedBaseX",
    baseUpdateYProperty: firstGroup(wbp, 302) ?? "UpdatedBaseY",
    angleInputId: firstInt(rot, 92) ?? 0,
    angleDeltaProperty: firstGroup(rot, 301) ?? "AngleDelta",
  };
}

// ─── 6. Flip Action ──────────────────────────────────────────────────

export function acDbBlockFlipActionParser(
  obj: DxfDynamicBlockObject,
): FlipAuthoringAction {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readActionCommon(obj, classes);
  const flip = classes.get("AcDbBlockFlipAction") ?? [];

  return {
    kind: "flip",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    flags: common.flags,
    entityHandles: common.entityHandles,
    displayAnchor: common.displayAnchor,
    parameterId: "",
    flipInputId: firstInt(flip, 92) ?? 0,
    flipDeltaProperty: firstGroup(flip, 301) ?? "UpdatedFlip",
  };
}

// ─── 7. Array Action ─────────────────────────────────────────────────

export function acDbBlockArrayActionParser(
  obj: DxfDynamicBlockObject,
): ArrayAuthoringAction {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readActionCommon(obj, classes);
  const arr = classes.get("AcDbBlockArrayAction") ?? [];

  return {
    kind: "array",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    flags: common.flags,
    entityHandles: common.entityHandles,
    displayAnchor: common.displayAnchor,
    parameterId: "",
    inputNodeIds: [
      firstInt(arr, 92) ?? 0,
      firstInt(arr, 93) ?? 0,
      firstInt(arr, 94) ?? 0,
      firstInt(arr, 95) ?? 0,
    ],
    baseProperty: firstGroup(arr, 301) ?? "Base",
    endProperty: firstGroup(arr, 302) ?? "End",
    updatedBaseProperty: firstGroup(arr, 303) ?? "UpdatedBase",
    updatedEndProperty: firstGroup(arr, 304) ?? "UpdatedEnd",
    columnDistance: firstNumber(arr, 140) ?? 0,
    rowDistance: firstNumber(arr, 141) ?? 0,
  };
}

// ─── 8. Lookup Action ────────────────────────────────────────────────

export function acDbBlockLookupActionParser(
  obj: DxfDynamicBlockObject,
): LookupAuthoringAction {
  const classes = partitionSubclasses(obj.rawGroups);
  const common = readActionCommon(obj, classes);
  const lookup = classes.get("AcDbBlockLookupAction") ?? [];

  // Lookup action layout (spec §4.5.4 + observed fixture):
  //   92 inputCount
  //   93 columnCount (typically 2)
  //   301 header
  //   302 × (rowCount × columnCount) cell values (input row first, then output)
  //   per-column metadata × columnCount:
  //     303 colLabel
  //     94 inputNodeId
  //     95 flag1
  //     96 flag2
  //     282 columnType (0/1)
  //     305 typeStr
  //     281 typeFlag
  //     304 deltaProperty
  //   280 closingFlag
  let rowCount = 0;
  let columnCount = 2;
  let header = "";
  const cellValues: string[] = [];
  const columnLabels: string[] = [];
  const columnInputNodeIds: number[] = [];
  const columnFlags1: number[] = [];
  const columnFlags2: number[] = [];
  const columnTypes: number[] = [];
  const columnTypeStrings: string[] = [];
  const columnTypeFlags: number[] = [];
  const columnDeltaProperties: string[] = [];
  let closingFlag = 1;

  // Walk the slice sequentially.
  let i = 0;
  // First read 92 (rowCount), 93 (columnCount), 301 (header).
  while (i < lookup.length) {
    const g = lookup[i];
    if (g && g.code === 92) {
      rowCount = Number.parseInt(g.value, 10);
      i += 1;
      break;
    }
    i += 1;
  }
  while (i < lookup.length) {
    const g = lookup[i];
    if (g && g.code === 93) {
      columnCount = Number.parseInt(g.value, 10);
      i += 1;
      break;
    }
    i += 1;
  }
  while (i < lookup.length) {
    const g = lookup[i];
    if (g && g.code === 301) {
      header = decodeDxfUnicode(g.value); // T9 PR-3 — lookup header decode
      i += 1;
      break;
    }
    i += 1;
  }
  // Now consume rowCount × columnCount × 302 cells.
  const totalCells = rowCount * columnCount;
  let consumed = 0;
  while (i < lookup.length && consumed < totalCells) {
    const g = lookup[i];
    if (g && g.code === 302) {
      cellValues.push(decodeDxfUnicode(g.value)); // T9 PR-3 — lookup cell value decode
      consumed += 1;
    }
    i += 1;
  }
  // Now per-column metadata × columnCount.
  let col = 0;
  while (col < columnCount && i < lookup.length) {
    let label = "";
    let inputId = 0;
    let f1 = 0;
    let f2 = 0;
    let cType = 0;
    let typeStr = "Custom";
    let tFlag = 0;
    let deltaProp = "";

    // Each column: 303 label, 94 input, 95/96 flags, 282 type,
    // 305 typeStr, 281 typeFlag, 304 delta. They are emitted in
    // order so a sequential walk works.
    let seen = 0;
    let breakOuter = false;
    while (i < lookup.length && seen < 8) {
      const g = lookup[i];
      if (!g) {
        i += 1;
        continue;
      }
      if (g.code === 303) { label = decodeDxfUnicode(g.value); seen += 1; } // T9 PR-3 — column label decode
      else if (g.code === 94) { inputId = Number.parseInt(g.value, 10); seen += 1; }
      else if (g.code === 95) { f1 = Number.parseInt(g.value, 10); seen += 1; }
      else if (g.code === 96) { f2 = Number.parseInt(g.value, 10); seen += 1; }
      else if (g.code === 282) { cType = Number.parseInt(g.value, 10); seen += 1; }
      else if (g.code === 305) { typeStr = decodeDxfUnicode(g.value); seen += 1; } // T9 PR-3 — type string decode (no-op on enum-ish defaults)
      else if (g.code === 281) { tFlag = Number.parseInt(g.value, 10); seen += 1; }
      else if (g.code === 304) { deltaProp = decodeDxfUnicode(g.value); seen += 1; } // T9 PR-3 — delta property name decode
      // 280 closing flag = end of all column metadata.
      if (g.code === 280) {
        closingFlag = Number.parseInt(g.value, 10);
        breakOuter = true;
        break;
      }
      i += 1;
      if (seen === 8) break;
    }
    columnLabels.push(label);
    columnInputNodeIds.push(inputId);
    columnFlags1.push(f1);
    columnFlags2.push(f2);
    columnTypes.push(cType);
    columnTypeStrings.push(typeStr);
    columnTypeFlags.push(tFlag);
    columnDeltaProperties.push(deltaProp);
    col += 1;
    if (breakOuter) break;
  }
  // Read trailing 280 if not yet seen.
  while (i < lookup.length) {
    const g = lookup[i];
    if (g && g.code === 280) {
      closingFlag = Number.parseInt(g.value, 10);
      break;
    }
    i += 1;
  }

  // Build cells matrix [rows][cols].
  const cells: string[][] = [];
  for (let r = 0; r < rowCount; r += 1) {
    const row: string[] = [];
    for (let c = 0; c < columnCount; c += 1) {
      row.push(cellValues[r * columnCount + c] ?? "");
    }
    cells.push(row);
  }
  // Build columns metadata array.
  const columns: LookupColumn[] = [];
  for (let c = 0; c < columnCount; c += 1) {
    columns.push({
      label: columnLabels[c] ?? "",
      inputNodeId: columnInputNodeIds[c] ?? 0,
      flag1: columnFlags1[c] ?? 0,
      flag2: columnFlags2[c] ?? 0,
      columnType: columnTypes[c] ?? 0,
      typeString: columnTypeStrings[c] ?? "Custom",
      typeFlag: columnTypeFlags[c] ?? 0,
      deltaProperty: columnDeltaProperties[c] ?? "",
    });
  }

  return {
    kind: "lookup",
    id: obj.handle,
    handle: obj.handle,
    evalGraphHandle: obj.ownerHandle ?? "",
    nodeId: common.evalExprNodeId,
    name: common.name,
    flags: common.flags,
    entityHandles: common.entityHandles,
    displayAnchor: common.displayAnchor,
    parameterId: "",
    rowCount,
    columnCount,
    header,
    cells,
    columns,
    closingFlag,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// EVAL GRAPH PARSER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Parse the AcDbEvalGraph topology from an ACAD_EVALUATION_GRAPH
 * object's raw groups.
 *
 * Per spec §4.4: 96 = node count, 97 = edge count, then per node:
 *   91 nodeId, 93 typeFlag, 95 paramInputId, 360 childHandle,
 *   92 in_a, 92 in_b, 92 out_a, 92 out_b
 */
export function parseEvalGraph(obj: DxfDynamicBlockObject): EvalGraph {
  const classes = partitionSubclasses(obj.rawGroups);
  const eg = classes.get("AcDbEvalGraph") ?? [];
  const nodes: EvalGraphNode[] = [];

  // Walk sequentially: 96 (nodeCount) -> 97 (edgeCount) -> per-node block.
  let i = 0;
  let nodeCount = 0;
  while (i < eg.length) {
    const g = eg[i];
    if (g && g.code === 96) {
      nodeCount = Number.parseInt(g.value, 10);
      i += 1;
      break;
    }
    i += 1;
  }
  while (i < eg.length) {
    const g = eg[i];
    if (g && g.code === 97) {
      i += 1;
      break;
    }
    i += 1;
  }

  for (let n = 0; n < nodeCount && i < eg.length; n += 1) {
    let nodeId = 0;
    let typeFlag = 32;
    let paramInputId = 0;
    let childHandle = "";
    const ninNums: number[] = [];

    // Each node: 91 nodeId, 93 typeFlag, 95 paramInputId, 360 child, 92×4 edges.
    let fieldCount = 0;
    while (i < eg.length && fieldCount < 8) {
      const g = eg[i];
      if (!g) {
        i += 1;
        continue;
      }
      if (g.code === 91) {
        nodeId = Number.parseInt(g.value, 10);
        fieldCount += 1;
      } else if (g.code === 93) {
        typeFlag = Number.parseInt(g.value, 10);
        fieldCount += 1;
      } else if (g.code === 95) {
        paramInputId = Number.parseInt(g.value, 10);
        fieldCount += 1;
      } else if (g.code === 360) {
        childHandle = g.value;
        fieldCount += 1;
      } else if (g.code === 92) {
        ninNums.push(Number.parseInt(g.value, 10));
        fieldCount += 1;
      } else {
        // Unknown / future code — skip without consuming a field slot.
      }
      i += 1;
      if (ninNums.length === 4) break;
    }

    nodes.push({
      nodeId,
      typeFlag,
      paramInputId,
      childHandle,
      incoming: [ninNums[0] ?? -1, ninNums[1] ?? -1],
      outgoing: [ninNums[2] ?? -1, ninNums[3] ?? -1],
    });
  }

  // Derive edges from outgoing slots.
  const edges: EvalGraph["edges"] = [];
  for (const node of nodes) {
    for (const out of node.outgoing) {
      if (out >= 0 && out !== node.nodeId) {
        edges.push({ fromNodeId: node.nodeId, toNodeId: out });
      }
    }
  }

  return evalGraphSchema.parse({
    handle: obj.handle,
    ownerHandle: obj.ownerHandle ?? "",
    nodes,
    edges,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// DISPATCH TABLE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Map from DXF class name (un-prefixed) → parameter parser function.
 */
const PARAMETER_PARSERS: Record<
  string,
  (obj: DxfDynamicBlockObject) => BlockAuthoringParameter
> = {
  BLOCKPOINTPARAMETER: acDbBlockPointParameterParser,
  BLOCKLINEARPARAMETER: acDbBlockLinearParameterParser,
  BLOCKPOLARPARAMETER: acDbBlockPolarParameterParser,
  BLOCKXYPARAMETER: acDbBlockXyParameterParser,
  BLOCKROTATIONPARAMETER: acDbBlockRotationParameterParser,
  BLOCKALIGNMENTPARAMETER: acDbBlockAlignmentParameterParser,
  BLOCKFLIPPARAMETER: acDbBlockFlipParameterParser,
  BLOCKVISIBILITYPARAMETER: acDbBlockVisibilityParameterParser,
  BLOCKLOOKUPPARAMETER: acDbBlockLookUpParameterParser,
  BLOCKBASEPOINTPARAMETER: acDbBlockBasepointParameterParser,
};

/**
 * Map from DXF class name → action parser function.
 */
const ACTION_PARSERS: Record<
  string,
  (obj: DxfDynamicBlockObject) => BlockAuthoringAction
> = {
  BLOCKMOVEACTION: acDbBlockMoveActionParser,
  BLOCKSCALEACTION: acDbBlockScaleActionParser,
  BLOCKSTRETCHACTION: acDbBlockStretchActionParser,
  BLOCKPOLARSTRETCHACTION: acDbBlockPolarStretchActionParser,
  BLOCKROTATEACTION: acDbBlockRotationActionParser,
  BLOCKFLIPACTION: acDbBlockFlipActionParser,
  BLOCKARRAYACTION: acDbBlockArrayActionParser,
  BLOCKLOOKUPACTION: acDbBlockLookupActionParser,
};

/**
 * Try to parse an OBJECT as an authoring parameter. Returns undefined
 * when the object's class isn't a recognised parameter class.
 */
export function tryParseAuthoringParameter(
  obj: DxfDynamicBlockObject,
): BlockAuthoringParameter | undefined {
  const cls = canonicalClassName(obj.type);
  const parser = PARAMETER_PARSERS[cls];
  return parser ? parser(obj) : undefined;
}

/**
 * Try to parse an OBJECT as an authoring action. Returns undefined
 * when the object's class isn't a recognised action class.
 */
export function tryParseAuthoringAction(
  obj: DxfDynamicBlockObject,
): BlockAuthoringAction | undefined {
  const cls = canonicalClassName(obj.type);
  const parser = ACTION_PARSERS[cls];
  return parser ? parser(obj) : undefined;
}

/**
 * Set of all authoring parameter class names (for upstream filtering).
 */
export const AUTHORING_PARAMETER_CLASSES: ReadonlySet<string> = new Set(
  Object.keys(PARAMETER_PARSERS),
);

/**
 * Set of all authoring action class names (for upstream filtering).
 */
export const AUTHORING_ACTION_CLASSES: ReadonlySet<string> = new Set(
  Object.keys(ACTION_PARSERS),
);
